import React, { useEffect, useState, useRef } from "react";
import { listSops, reviewSop } from "../services/api";
import { Sop } from "../types/sop";
import { SopCard } from "../components/SopCard";

const POLL_INTERVAL = 10_000;

export function ReviewPage() {
  const [sops, setSops] = useState<Sop[]>([]);
  const [stationFilter, setStationFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  const load = async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    setError("");
    try {
      const data = await listSops(stationFilter || undefined);
      setSops(data);
    } catch (e: any) {
      setError(e.message || "Failed to load SOPs");
    }
    setLoading(false);
  };

  // Initial load + auto-poll when there are processing SOPs
  useEffect(() => {
    load(true);
    return () => clearInterval(timerRef.current);
  }, []); // eslint-disable-line

  useEffect(() => {
    clearInterval(timerRef.current);
    const hasProcessing = sops.some((s) => s.status === "transcribing");
    if (hasProcessing) {
      timerRef.current = setInterval(() => load(), POLL_INTERVAL);
    }
    return () => clearInterval(timerRef.current);
  }, [sops]); // eslint-disable-line

  const handleReview = async (sop: Sop, action: "approved" | "rejected") => {
    try {
      await reviewSop(sop.sop_id, sop.station_id, action);
      setSops((prev) => prev.map((s) => s.sop_id === sop.sop_id ? { ...s, status: action } : s));
    } catch (e: any) {
      setError(`Failed to ${action === "approved" ? "approve" : "reject"} SOP: ${e.message}`);
    }
  };

  const processing = sops.filter((s) => s.status === "transcribing");
  const errored = sops.filter((s) => s.status === "error");
  const pending = sops.filter((s) => s.status === "pending_review");
  const reviewed = sops.filter((s) => s.status === "approved" || s.status === "rejected");

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: 24 }}>
      <h2>SOP Review Dashboard</h2>

      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        <input value={stationFilter} onChange={(e) => setStationFilter(e.target.value)}
          placeholder="Filter by station ID" style={{ flex: 1, padding: 8 }} />
        <button onClick={() => load(true)} style={{ padding: "8px 16px" }}>Search</button>
      </div>

      {error && (
        <div style={{ background: "#ffebee", color: "#c62828", padding: 12, borderRadius: 8, marginBottom: 16 }}>
          ⚠️ {error}
        </div>
      )}

      {loading ? <p>Loading...</p> : (
        <>
          {processing.length > 0 && (
            <>
              <h3>⏳ Processing ({processing.length})</h3>
              <p style={{ color: "#666", fontSize: 13, marginTop: -8 }}>Auto-refreshing every 10 seconds...</p>
              {processing.map((sop) => (
                <div key={sop.sop_id} style={{ border: "1px solid #bbdefb", background: "#e3f2fd", borderRadius: 8, padding: 16, marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>🔄</span>
                    <strong>{sop.station_id}</strong> — {sop.process_area || "processing"} · {sop.metadata?.role || ""}
                  </div>
                  <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>SOP ID: {sop.sop_id}</p>
                </div>
              ))}
            </>
          )}

          {errored.length > 0 && (
            <>
              <h3 style={{ color: "#c62828" }}>❌ Failed ({errored.length})</h3>
              {errored.map((sop) => (
                <div key={sop.sop_id} style={{ border: "1px solid #ef9a9a", background: "#ffebee", borderRadius: 8, padding: 16, marginBottom: 12 }}>
                  <strong>{sop.station_id}</strong> — {sop.process_area || "unknown"}
                  <p style={{ margin: "4px 0 0", color: "#c62828", fontSize: 13 }}>
                    Generation failed. Try re-recording this SOP. (ID: {sop.sop_id})
                  </p>
                </div>
              ))}
            </>
          )}

          <h3>Pending Review ({pending.length})</h3>
          {pending.length === 0 && <p style={{ color: "#999" }}>No SOPs pending review.</p>}
          {pending.map((sop) => (
            <SopCard key={sop.sop_id} sop={sop}
              onApprove={() => handleReview(sop, "approved")}
              onReject={() => handleReview(sop, "rejected")} />
          ))}

          <h3 style={{ marginTop: 32 }}>Reviewed ({reviewed.length})</h3>
          {reviewed.map((sop) => (
            <SopCard key={sop.sop_id} sop={sop} onApprove={() => {}} onReject={() => {}} />
          ))}
        </>
      )}
    </div>
  );
}
